import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import { Loader2, Save, Sparkles, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const toLines = (a) => (Array.isArray(a) ? a.join("\n") : a || "");
const fromLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const LIST_FIELDS = ["subjects", "colors", "required_imagery", "prohibited_imagery"];
const AREA_FIELDS = ["visual_direction", "additional_ai_prompt"];

const FIELDS = [
  ["display_headline", "Display Headline"], ["display_support", "Display Supporting Line"], ["display_cta", "Display CTA"],
  ["url", "URL"], ["price", "Price"], ["audience", "Audience"], ["offer_focus", "Offer Focus"],
  ["visual_direction", "Visual Direction"], ["style", "Style"], ["setting", "Setting"], ["layout", "Layout"],
  ["subjects", "Subjects"], ["colors", "Colors"], ["required_imagery", "Required Imagery"],
  ["prohibited_imagery", "Prohibited Imagery"], ["language", "Language / Tone"], ["additional_ai_prompt", "Additional AI Prompt"],
];

export default function CreativeBrief({ campaignId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [use, setUse] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [overridden, setOverridden] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/marketing/campaigns/${campaignId}/creative-brief`).then((r) => {
    setData(r.data); setUse(r.data.brief.use_overrides);
    setOverrides(r.data.brief.overrides || {}); setOverridden(r.data.brief.overridden || {});
  }).catch(() => setData(false));
  useEffect(() => { if (open && !data) load(); /* eslint-disable-next-line */ }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.put(`/marketing/campaigns/${campaignId}/creative-brief`,
        { use_overrides: use, overrides, overridden });
      toast.success(d.note || "Creative brief saved");
      setData((s) => ({ ...s, brief: d.brief, effective: d.effective }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const setVal = (f, v) => setOverrides((s) => ({ ...s, [f]: v }));
  const toggleOv = (f, on) => setOverridden((s) => ({ ...s, [f]: on }));

  return (
    <div className="mt-6 bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="creative-brief-card">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-6 py-4" data-testid="creative-brief-toggle">
        <span className="flex items-center gap-2 font-heading font-bold"><Sparkles className="h-4 w-4 text-[#581C87]" /> Creative Brief</span>
        {open ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
      </button>

      {open && (data === null ? (
        <div className="px-6 py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#581C87]" /></div>
      ) : data === false ? (
        <p className="px-6 py-4 text-sm text-red-600">Failed to load creative brief.</p>
      ) : (
        <div className="px-6 pb-6 space-y-4">
          <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 bg-gray-50">
            <div>
              <p className="text-sm font-medium">Use campaign overrides</p>
              <p className="text-xs text-gray-500">Off = inherit all global Media Settings. On = override selected fields for this campaign only. Saving does not change existing media.</p>
            </div>
            <Switch checked={use} onCheckedChange={setUse} data-testid="brief-use-overrides" />
          </div>

          {use && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FIELDS.map(([f, label]) => {
                const isList = LIST_FIELDS.includes(f);
                const isArea = AREA_FIELDS.includes(f) || isList;
                const on = !!overridden[f];
                const inherited = data.effective?.[f];
                const placeholder = Array.isArray(inherited) ? inherited.join(", ") : (inherited || "inherited");
                return (
                  <div key={f} className="space-y-1.5 border border-gray-100 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold">{label}</Label>
                      <label className="flex items-center gap-1.5 text-[11px] text-gray-500 cursor-pointer">
                        <Checkbox checked={on} onCheckedChange={(v) => toggleOv(f, !!v)} data-testid={`brief-override-${f}`} /> Override
                      </label>
                    </div>
                    {isArea ? (
                      <Textarea disabled={!on} value={isList ? toLines(overrides[f]) : (overrides[f] || "")}
                        onChange={(e) => setVal(f, isList ? fromLines(e.target.value) : e.target.value)}
                        placeholder={on ? "" : `Inherited: ${placeholder}`} className="text-sm focus-visible:ring-[#581C87]"
                        data-testid={`brief-field-${f}`} />
                    ) : (
                      <Input disabled={!on} value={overrides[f] || ""} onChange={(e) => setVal(f, e.target.value)}
                        placeholder={on ? "" : `Inherited: ${placeholder}`} className="text-sm focus-visible:ring-[#581C87]"
                        data-testid={`brief-field-${f}`} />
                    )}
                    {!on && <p className="text-[11px] text-emerald-600">Inherited from global</p>}
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="save-creative-brief-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Save as Campaign Default</>}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
